import React, { useState, useEffect } from 'react';
import { getToken } from "../rtkq/services/localStorageService";
import { useGetLoggedUserQuery } from '../rtkq/services/userAuthApi';

// const BACKEND_BASE_URL = 'https://ft-cloud-run-200839435575.us-central1.run.app';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_SERVER_URL || "http://127.0.0.1:8000";

const StudentPerformance = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const { access_token } = getToken();
  const { data: userData } = useGetLoggedUserQuery(access_token);

  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        //fetch BACKEND_BASE_URL + /api/chat/history/
        const response = await fetch(`${BACKEND_BASE_URL}/api/chat/history/`, {
          headers: {
            'Authorization': `Bearer ${access_token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          // Using ChatHistorySerializer format - array of chat entries with user info
          processChatHistorySerializerData(data);
        } else {
          throw new Error("Unexpected data format from API");
        }
        
        setError(null);
      } catch (error) {
        console.error('Error fetching chat history:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    // Process data from ChatHistorySerializer (which includes full user details)
    const processChatHistorySerializerData = (chatEntries) => {
      // Group chats by user ID
      const chatsByUser = {};
      
      chatEntries.forEach(entry => {
        const userId = entry.user.id;
        
        if (!chatsByUser[userId]) {
          // Initialize user entry if it doesn't exist
          chatsByUser[userId] = {
            id: userId,
            userDetails: {
              first_name: entry.user.first_name,
              last_name: entry.user.last_name,
              email: entry.user.email,
              student_details: entry.user.student_details || {}
            },
            chats: [],
            overall_rating: 0,
            ratingSum: 0,
            ratingCount: 0
          };
        }
        
        // Add chat to user's chats array
        chatsByUser[userId].chats.push({
          id: entry.id,
          message: entry.message,
          response: entry.response,
          rating: entry.message_rating || 0,
          timestamp: entry.timestamp,
          mode: entry.mode || 'chat',
          subtype: entry.subtype || 'general'
        });
        
        // Update rating stats
        if (entry.message_rating) {
          chatsByUser[userId].ratingSum += entry.message_rating;
          chatsByUser[userId].ratingCount += 1;
        }
      });
      
      // Calculate overall ratings and convert to array
      const processedHistory = Object.values(chatsByUser).map(user => {
        // Calculate average rating if we have ratings
        if (user.ratingCount > 0) {
          // user.overall_rating = (user.ratingSum / user.ratingCount).toFixed(1);
          user.overall_rating = (user.ratingSum).toFixed(1);
        }
        
        // Remove temporary calculation fields
        delete user.ratingSum;
        delete user.ratingCount;
        
        return user;
      });
      
      // Sort alphabetically by student name
      processedHistory.sort((a, b) => {
        const nameA = `${a.userDetails.first_name} ${a.userDetails.last_name}`.toLowerCase();
        const nameB = `${b.userDetails.first_name} ${b.userDetails.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setChatHistory(processedHistory);
      
      // Set the first student as selected by default if we have data
      if (processedHistory.length > 0 && !selectedStudent) {
        setSelectedStudent(processedHistory[0].id);
      }
    };
    

    if (userData?.role === 'teacher') {
      fetchChatHistory();
    }
  }, [access_token, userData, selectedStudent]);

  // Handler for student selection
  const handleStudentChange = (studentId) => {
    setSelectedStudent(Number(studentId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 border-t-2 border-t-transparent"></div>
        <span className="ml-3 text-purple-600 font-medium">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 mx-auto max-w-2xl bg-red-50 rounded-lg border border-red-200 shadow-sm">
        <div className="flex items-center text-red-600 font-medium text-center mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Error loading chat history
        </div>
        <p className="text-red-700">{error}</p>
        <p className="mt-2 text-sm text-red-500">Please check the console for more details.</p>
      </div>
    );
  }

  if (userData?.role !== 'teacher') {
    return (
      <div className="p-6 mx-auto max-w-lg bg-red-50 rounded-lg border border-red-200 shadow-sm text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <h2 className="text-xl font-bold text-red-700 mb-2">Unauthorized Access</h2>
        <p className="text-red-600">This dashboard is only accessible to teachers.</p>
      </div>
    );
  }

  // Find the selected student data
  const selectedStudentData = chatHistory.find(student => student.id === selectedStudent);

  return (
    <div className="container mx-auto p-4 space-y-6">
      <h1 className="text-3xl font-bold mb-8 text-center text-red-600 py-2 border-b-2 border-green-500">
        <span className="inline-block mr-3">📊</span>
        Student Performance Dashboard
      </h1>
      
      {chatHistory.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-gray-500 font-medium">No chat history available</p>
          <p className="text-gray-400 text-sm mt-2">Student conversations will appear here once available</p>
        </div>
      ) : (
        <>
          {/* Student Selector Dropdown */}
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <label htmlFor="student-select" className="font-medium text-gray-700">
                Select Student:
              </label>
              <div className="relative">
                <select
                  id="student-select"
                  value={selectedStudent || ''}
                  onChange={(e) => handleStudentChange(e.target.value)}
                  className="block w-64 pl-4 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md"
                >
                  {chatHistory.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.userDetails.first_name} {student.userDetails.last_name} ({student.userDetails.email})
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Student's Data */}
          {selectedStudentData && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-50 to-yellow-50 p-5 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center">
                    <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center mr-3 font-bold">
                      {selectedStudentData.userDetails.first_name.charAt(0)}{selectedStudentData.userDetails.last_name.charAt(0)}
                    </div>
                    <div>
                      <span className="text-xl font-semibold text-purple-900 block">
                        {selectedStudentData.userDetails.first_name} {selectedStudentData.userDetails.last_name}
                      </span>
                      <span className="text-gray-600 text-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {selectedStudentData.userDetails.email}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-white rounded-lg shadow-sm border border-purple-200">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Overall Rating</div>
                    <div className="text-purple-600 font-bold text-2xl flex items-center">
                      {selectedStudentData.overall_rating}
                      <span className="text-yellow-400 ml-1">★</span>
                      <span className="text-sm text-gray-500 ml-1 font-normal"></span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  {selectedStudentData.chats
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((chat) => (
                      <div key={chat.id} className="border-t pt-4">
                        <div className="flex justify-between items-center mb-3">
                          <div className="space-x-4">
                            <span className="text-sm text-gray-500">
                              {new Date(chat.timestamp).toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-600">
                              {chat.mode} / {chat.subtype}
                            </span>
                          </div>
                          <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                            Rating: {chat.rating}/10
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-purple-100 p-4 rounded-lg border-2 border-purple-700">
                            <p className="text-sm font-medium text-purple-800 mb-2">
                              Student Message:
                            </p>
                            <p className="text-gray-800">{chat.message}</p>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300">
                            <p className="text-sm font-medium text-yellow-700 mb-2">
                              <strong>AI Response:</strong>
                            </p>
                            <p className="text-gray-800">{chat.response}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentPerformance;